import React, { useEffect, useReducer } from 'react'

import { Box, Button, Tooltip } from '@mui/material'
import ReactHtmlParser from 'react-html-parser'

import ReplyAllIcon from '@mui/icons-material/ReplyAll'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt'
import ReplyIcon from '@mui/icons-material/Reply'

import { cortexPatch } from './cortexFetch'
import defaultReducer from '../../utils/defaultReducer'
import useNotification from '../Notification'

const LINK_RE = /(www\.[^\s]+)|(http|ftp|https):\/\/(www\.)?([\w_-]+(?:\.[\w_-]+)+)([\w.,\/@?^=%&:~+#-]*[\w\/@?^=%&:~+#-])/g

const htmlEntities = (str) => String(str)
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

const replaceLinkWithAnchor = (string) => {
    const trailingPunctuation = string.match(/[),.;!?]+$/)?.[0] ?? ''
    const link = trailingPunctuation ? string.slice(0, -trailingPunctuation.length) : string
    const href = link.startsWith('www.') ? `https://${link}` : link
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" style='color:white'>${link}</a>${trailingPunctuation}`
}

const transformTextNode = (node, index) => {
    if (node.type === 'text')
        return (<span key={`msgspan-${index}`} style={{ whiteSpace: 'inherit' }}>{node.data}</span>)
}

const renderMessageText = (text) => ReactHtmlParser(
    htmlEntities(text).replace(LINK_RE, replaceLinkWithAnchor),
    { transform: transformTextNode }
)

// Ported from kwik-assistant's MessageItem, unchanged: sending and copying
// remain two distinct, ungated actions, independent from the good/bad
// rating. See spec_curadoria.md for the eventual (deferred) reconciliation.
const MessageItem = ({ message, assistantUrl, accessToken, onSend, onCopy, onAction }) => {
    const [state, setState] = useReducer(defaultReducer, { ...message })

    // A streamed message's final text is set once via a parent state update
    // after the stream ends - this component only reads `message` on mount,
    // so without this sync, send/copy would act on the truncated first chunk.
    useEffect(() => {
        if (message.text !== state.text)
            setState({ text: message.text })
    }, [message.text, state.text])

    const markUsed = () => cortexPatch(`${assistantUrl}/ai/questions/used_answer/${state.id}`, accessToken, {})
    const markBad = () => cortexPatch(`${assistantUrl}/ai/questions/bad_answer/${state.id}`, accessToken, {})

    const copyToClipboard = (bad = false) => {
        if (!navigator.clipboard?.writeText) {
            console.error('Clipboard API not supported or unavailable')
            return
        }
        const txt = document.createElement('textarea')
        txt.innerHTML = state.text
        navigator.clipboard.writeText(txt.value).then(() => {
            if (!bad) useNotification('Sucesso', 'Resposta copiada para o clipboard.', 'success')
        }).catch((err) => console.error('Failed to copy message: ', err))
    }

    const sendNow = () => {
        markUsed()
        onAction?.(state, 'SEND_NOW')
        if (onSend) onSend(state)
        else console.error('No onSend handler provided to MessageItem')
    }

    const copyForEditing = () => {
        copyToClipboard()
        markUsed()
        onAction?.(state, 'COPY_TO_EDIT')
        if (onCopy) onCopy(state)
        else console.error('No onCopy handler provided to MessageItem')
    }

    const markGood = () => {
        markUsed()
        onAction?.(state, 'MARK_GOOD')
        useNotification('Sucesso', 'Resposta enviada para curadoria.', 'success')
        setState({ messageInteracted: true, messageAccepted: true })
    }

    const markBadAndCopy = () => {
        markBad()
        onAction?.(state, 'MARK_BAD')
        useNotification('Sucesso', 'Resposta enviada para curadoria.', 'success')
        setState({ messageInteracted: true, messageRejected: true })
        copyToClipboard(true)
    }

    if (state.userId === 1) {
        return (
            <Box className="cortex-messageitem user">
                {renderMessageText(state.text)}
            </Box>
        )
    }

    return (
        <Box className="cortex-messageitem gpt" id={`msg-${state.id}`}>
            {renderMessageText(state.text)}
            <Box className="cortex-interactionbuttons">
                <Box sx={{ display: 'flex' }}>
                    <Tooltip title="Enviar resposta">
                        <Button onClick={sendNow} color="success" className="cortex-iconbutton">
                            <ReplyAllIcon />
                        </Button>
                    </Tooltip>
                    <Tooltip title="Copiar resposta">
                        <Button onClick={copyForEditing} color="success" className="cortex-iconbutton">
                            <ReplyIcon />
                        </Button>
                    </Tooltip>
                </Box>
                <Box sx={{ display: 'flex' }}>
                    <Tooltip title="Resposta adequada">
                        <Button
                            onClick={markGood}
                            disabled={state.messageInteracted}
                            color="success"
                            className={`cortex-iconbutton ${state.messageAccepted ? 'green' : ''}`}
                        >
                            <ThumbUpAltIcon />
                        </Button>
                    </Tooltip>
                    <Tooltip title="Resposta inapropriada">
                        <Button
                            onClick={markBadAndCopy}
                            color="error"
                            disabled={state.messageInteracted}
                            className={`cortex-iconbutton ${state.messageRejected ? 'red' : ''}`}
                        >
                            <ThumbDownIcon />
                        </Button>
                    </Tooltip>
                </Box>
            </Box>
        </Box>
    )
}

export default MessageItem
